import { usePermissions } from '@customerio/hydra-permissions/react';
import type { Delivery } from '@/services/db/delivery/entity';
import { useCampaignOptional } from '@/services/db/campaign/queries';
import { useNewsletterOptional } from '@/services/db/newsletter/queries';
import { useTransactionalMessageOptional } from '@/services/db/transactional-message/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { generatePath } from '@/services/router/generate-path';

/**
 * The message a delivery belongs to, resolved for the expanded row's related
 * link. Links target the embedded Journeys routes (still Ember-owned), so they
 * go through `generatePath`.
 */
export type RelatedModel =
  | { kind: 'campaign'; name: string; href: string; isApiTriggered: boolean }
  | { kind: 'newsletter'; name: string; href: string }
  | { kind: 'transactional'; name: string; href: string }
  | { kind: 'none' };

export interface RelatedModelResult {
  model: RelatedModel;
  isLoading: boolean;
}

/**
 * Resolve a delivery's parent message (campaign / newsletter / transactional)
 * for the related link. Only the FK that's set fires a fetch; the others stay
 * dormant. A delivery carries at most one of these ids.
 */
export function useDeliveryRelatedModel(delivery: Delivery): RelatedModelResult {
  const workspaceId = requireWorkspaceId();
  const campaignId = delivery.campaign_id;
  const newsletterId = delivery.newsletter_id;
  const transactionalId = delivery.transactional_message_id;

  const campaign = useCampaignOptional({
    workspaceId,
    id: campaignId != null ? String(campaignId) : '',
    enabled: campaignId != null,
  });
  const newsletter = useNewsletterOptional({
    workspaceId,
    id: newsletterId != null ? String(newsletterId) : '',
    enabled: newsletterId != null,
  });
  const transactional = useTransactionalMessageOptional({
    workspaceId,
    id: transactionalId != null ? String(transactionalId) : '',
    enabled: transactionalId != null,
  });

  if (campaignId != null) {
    const data = campaign.data;
    if (data == null) {
      return { model: { kind: 'none' }, isLoading: campaign.isLoading };
    }
    return {
      isLoading: false,
      model: {
        kind: 'campaign',
        name: data.name,
        isApiTriggered: data.type === 'triggered_broadcast',
        href: generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', { workspaceId, campaignId }),
      },
    };
  }

  if (newsletterId != null) {
    const data = newsletter.data;
    if (data == null) {
      return { model: { kind: 'none' }, isLoading: newsletter.isLoading };
    }
    return {
      isLoading: false,
      model: {
        kind: 'newsletter',
        name: data.name,
        href: generatePath('/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId', {
          workspaceId,
          newsletterId,
        }),
      },
    };
  }

  if (transactionalId != null) {
    const data = transactional.data;
    if (data == null) {
      return { model: { kind: 'none' }, isLoading: transactional.isLoading };
    }
    return {
      isLoading: false,
      model: {
        kind: 'transactional',
        name: data.name,
        href: generatePath('/workspaces/:workspaceId/journeys/transactional/:transactionalMessageId', {
          workspaceId,
          transactionalMessageId: transactionalId,
        }),
      },
    };
  }

  return { model: { kind: 'none' }, isLoading: false };
}

export interface DeliveryRowPermission {
  /** No edit access to the parent message — disables the action buttons. */
  hasReadOnlyPermission: boolean;
  /** The extra gate the backend enforces on resend (`POST deliveries/:id/redeliver`). */
  hasSendPermission: boolean;
}

/**
 * Per-row read-only permission, keyed on the parent message's edit permission
 * (a delivery isn't editable if its campaign / newsletter / transactional
 * isn't). Broadcasts (api-triggered campaigns) use the broadcasts permission.
 */
export function useDeliveryRowPermission(delivery: Delivery, model: RelatedModel): DeliveryRowPermission {
  const { hasPermission } = usePermissions();

  let hasReadOnlyPermission = false;
  if (delivery.newsletter_id != null) {
    hasReadOnlyPermission = !hasPermission('journeys/newsletters/edit');
  } else if (delivery.transactional_message_id != null) {
    hasReadOnlyPermission = !hasPermission('journeys/transactional_messages/edit');
  } else if (delivery.campaign_id != null) {
    const isBroadcast = model.kind === 'campaign' && model.isApiTriggered;
    hasReadOnlyPermission = !hasPermission(isBroadcast ? 'journeys/broadcasts/edit' : 'journeys/campaigns/edit');
  }

  return { hasReadOnlyPermission, hasSendPermission: hasPermission('journeys/deliveries/send') };
}
